import { defaultPotName } from "../constants";
import type { PeriodType, SpendingPot, SpendingPotTrackerApiResponse } from "../types";

export async function fetchSpendingPots() {
  const response = await fetch("/api/spending-pots", {
    credentials: "same-origin",
    headers: {
      Accept: "application/json"
    }
  });

  if (!response.ok) {
    throw new Error("Could not load spending pots.");
  }

  return (await response.json()) as SpendingPot[];
}

export async function fetchSpendingPotTrackerState(spendingPotId: string) {
  const response = await fetch(`/api/spending-pots/${spendingPotId}/tracker`, {
    credentials: "same-origin",
    headers: {
      Accept: "application/json"
    }
  });

  if (!response.ok) {
    throw new Error("Could not load tracker state.");
  }

  return (await response.json()) as SpendingPotTrackerApiResponse;
}

export async function postSpendingPot(request: { currencyCode: string; name: string }) {
  const response = await fetch("/api/spending-pots", {
    body: JSON.stringify(request),
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    method: "POST"
  });

  if (!response.ok) {
    const problem = await readValidationProblem(response);
    throw new Error(problem ?? "Could not create pot.");
  }

  return (await response.json()) as SpendingPot;
}

export async function postDefaultSpendingPot(currencyCode: string) {
  const response = await fetch("/api/spending-pots/default", {
    body: JSON.stringify({ currencyCode, name: defaultPotName }),
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    method: "POST"
  });

  if (!response.ok) {
    const problem = await readValidationProblem(response);
    throw new Error(problem ?? "Could not create default pot.");
  }

  return (await response.json()) as SpendingPot;
}

export async function postArchiveSpendingPot(spendingPotId: string) {
  const response = await fetch(`/api/spending-pots/${spendingPotId}/archive`, {
    credentials: "same-origin",
    headers: {
      Accept: "application/json"
    },
    method: "POST"
  });

  if (!response.ok) {
    const problem = await readValidationProblem(response);
    throw new Error(problem ?? "Could not archive pot.");
  }
}

export async function postRestoreSpendingPot(spendingPotId: string) {
  const response = await fetch(`/api/spending-pots/${spendingPotId}/restore`, {
    credentials: "same-origin",
    headers: {
      Accept: "application/json"
    },
    method: "POST"
  });

  if (!response.ok) {
    const problem = await readValidationProblem(response);
    throw new Error(problem ?? "Could not restore pot.");
  }

  return (await response.json()) as SpendingPot;
}

export async function postStartSpendingPotTracking(
  spendingPotId: string,
  request: { periodAllowance: number; periodType: PeriodType; startDateUtc: string }
) {
  const response = await fetch(`/api/spending-pots/${spendingPotId}/tracker/start`, {
    body: JSON.stringify(request),
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    method: "POST"
  });

  if (!response.ok) {
    const problem = await readValidationProblem(response);
    throw new Error(problem ?? "Could not start tracking.");
  }

  return (await response.json()) as SpendingPotTrackerApiResponse;
}

export async function postSpendingPotSpendEntry(
  spendingPotId: string,
  request: { amount: number; dayNumber: number }
) {
  const response = await fetch(`/api/spending-pots/${spendingPotId}/tracker/spend-entries`, {
    body: JSON.stringify(request),
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    method: "POST"
  });

  if (!response.ok) {
    const problem = await readValidationProblem(response);
    throw new Error(problem ?? "Could not save spend.");
  }

  return (await response.json()) as SpendingPotTrackerApiResponse;
}

export async function postSpendingPotSpendEntryAction(
  spendingPotId: string,
  action: "undo" | "redo",
  dayNumber: number
) {
  const response = await fetch(`/api/spending-pots/${spendingPotId}/tracker/spend-entries/${action}`, {
    body: JSON.stringify({ dayNumber }),
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    method: "POST"
  });

  if (!response.ok) {
    const problem = await readValidationProblem(response);
    throw new Error(problem ?? `Could not ${action} spend.`);
  }

  return (await response.json()) as SpendingPotTrackerApiResponse;
}

async function readValidationProblem(response: Response) {
  try {
    const body = (await response.json()) as { errors?: Record<string, string[]>; title?: string };
    const firstError = Object.values(body.errors ?? {})[0]?.[0];

    return firstError ?? body.title ?? null;
  } catch {
    return null;
  }
}
